import { useQueries } from 'react-query'
import type { HazardInfo } from '../../types/climate'
import type { MetricDataFilters } from '../analytics'
import { portfolioClimateDataQuery } from './index'
import {
  HAZARD_RISK_CONFIG,
  extractPropertyRisk,
  hazardRiskQueryParams,
  isHazardPeriodAvailable,
  summarizeHazardRisk,
  type PortfolioHazardRisk,
} from '../../components/climateAnalytics/portfolio/portfolioRisk'
import { getClimateHazardViewConfig } from '../../components/climateAnalytics/common/hazardConfig'
import type { AdaptationView, CanonicalPeriod, EmissionTier } from '../../components/climateAnalytics/common'

type Args = {
  hazards: HazardInfo[]
  groupId?: number
  propertyIds: number[]
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
  view: AdaptationView
  floodFilters?: MetricDataFilters | null
  enabled: boolean
}

export default function usePortfolioHazardRisks({
  hazards,
  groupId,
  propertyIds,
  emissionTier,
  selectedPeriod,
  view,
  floodFilters,
  enabled,
}: Args): PortfolioHazardRisk[] {
  const configuredHazards = hazards.filter(hazard => HAZARD_RISK_CONFIG[hazard.name])
  const queries = useQueries(
    configuredHazards.map(hazard => ({
      ...portfolioClimateDataQuery(
        HAZARD_RISK_CONFIG[hazard.name].metricKey,
        { groupId },
        {
          ...hazardRiskQueryParams(hazard.name, emissionTier, selectedPeriod, view),
          ...(hazard.name === 'fld' ? { filters: floodFilters ?? null } : {}),
        },
      ),
      enabled: enabled && propertyIds.length > 0 && isHazardPeriodAvailable(hazard.name, selectedPeriod),
      // Never label the preceding group's/pathway's/variant's results as current.
      keepPreviousData: false,
    })),
  )

  return hazards.map(hazard => {
    const config = HAZARD_RISK_CONFIG[hazard.name]
    const query = queries[configuredHazards.findIndex(item => item.name === hazard.name)]
    const periodAvailable = isHazardPeriodAvailable(hazard.name, selectedPeriod)
    const adaptationsEnabled =
      view === 'effective' && Boolean(getClimateHazardViewConfig(hazard.name).supportsAdaptations)
    const byProperty = Object.fromEntries(
      propertyIds.map(id => [
        id,
        extractPropertyRisk(
          hazard.name,
          periodAvailable && !query?.isError ? query?.data?.[id] : undefined,
          emissionTier,
          selectedPeriod,
          adaptationsEnabled,
        ),
      ]),
    )
    return {
      hazard,
      config,
      periodAvailable,
      adaptationsEnabled,
      byProperty,
      loading: Boolean(config && periodAvailable && (enabled ? query?.isLoading && propertyIds.length > 0 : true)),
      error: Boolean(query?.isError),
      retry: query
        ? () => {
            void query.refetch()
          }
        : undefined,
      summary: summarizeHazardRisk(propertyIds, byProperty),
    }
  })
}
